import React, { useContext, useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Cropper from "react-easy-crop";
import Swal from "sweetalert2";
import { UrlContext } from "../../contexts/UrlContext";

const ProfilePicture = ({
  expertDefaultImg,
  profilePicturePreview,
  setProfilePicturePreview,
  expertID,
  callingFrom='Profile',
}) => {
  const { API_URL } = useContext(UrlContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const dropArea = useRef(null);

  // Api for saving profile picture
  const updateProfilePicture = async (file) => {
    const token = localStorage.getItem("AccessToken");

    fetch(`${API_URL}update-expert-picture`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ expertID: expertID, profilePicture: file }),
    })
      .then((res) => res.json())
      .then((response) => {
        if (response.status === "success") {
          Swal.fire({
            title: "Updated",
            text: "Modification has been updated successfully!",
            icon: "success",
            showConfirmButton: false,
            timer: 1500,
          });
        } else {
          Swal.fire({
            title: "Error",
            text: "Failed to update profile picture",
            icon: "error",
            showConfirmButton: false,
            timer: 1500,
          });
        }
      })
      .catch(() =>
        Swal.fire({
          title: "Error",
          text: "Error while uploading!",
          icon: "error",
          showConfirmButton: false,
          timer: 1500,
        })
      );
  };

	// Generate cropped image as Base64
	const getCroppedImg = useCallback(async (imageSrc, pixelCrop) => {
		const image = new Image();
		image.crossOrigin = "anonymous";
		image.src = imageSrc;
		await new Promise((resolve) => (image.onload = resolve));

		const canvas = document.createElement("canvas");
		const ctx = canvas.getContext("2d");

		canvas.width = pixelCrop.width;
		canvas.height = pixelCrop.height;

		ctx.drawImage(
			image,
			pixelCrop.x,
			pixelCrop.y,
			pixelCrop.width,
			pixelCrop.height,
			0,
			0,
			pixelCrop.width,
			pixelCrop.height
		);

		// Return both Blob URL (for local preview) and Base64 (for backend)
		return new Promise((resolve) => {
			canvas.toBlob(
				async (blob) => {
					const blobUrl = URL.createObjectURL(blob);

					const reader = new FileReader();
					reader.onloadend = () => {
						const base64Data = reader.result;
						resolve({ blobUrl, base64Data });
					};
					reader.readAsDataURL(blob);
				},
				"image/jpeg",
				0.95 // quality
			);
		});
	}, []);

  // Update cropped area pixels when user stops moving/zooming
  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  // Handle file upload
  const handleFile = (file) => {
    if (file && file.type.startsWith("image/")) {
      const previewUrl = URL.createObjectURL(file);
      setSelectedImage(previewUrl);
    } else {
      Swal.fire({title: "Invalid File", text: "Please upload a valid image file.", icon: "warning", showConfirmButton: false, timer: 1500,});
    }
  };

  // Save cropped image
	const handleCropSave = async () => {
		if (!selectedImage || !croppedAreaPixels) return;
		const { blobUrl, base64Data } = await getCroppedImg(selectedImage, croppedAreaPixels);

		// Update preview with blob URL for immediate display
		setProfilePicturePreview(blobUrl);

		// Send base64 string to backend
		updateProfilePicture(base64Data);

		setIsModalOpen(false);
		setSelectedImage("");
	};

  // When modal opens, preload existing picture in cropper
  useEffect(() => {
    if (isModalOpen) {
      setSelectedImage(profilePicturePreview || expertDefaultImg);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
  }, [isModalOpen, profilePicturePreview, expertDefaultImg]);

  // Handle removing picture
  const handleRemovePicture = () => {
    Swal.fire({
      title: "Remove Profile Picture?",
      text: "This will remove your current photo and revert to default.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, remove it!",
    }).then((result) => {
      if (result.isConfirmed) {
        setProfilePicturePreview("");
        updateProfilePicture("");
        setSelectedImage(expertDefaultImg);
      }
    });
  };

	// Handle drag & drop - supports LinkedIn 
  const handleDrop = async (e) => {
    e.preventDefault();

    // CASE 1: Real file dropped from desktop / finder / explorer
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith("image/")) {
      handleFile(file);
      return;
    }

    // CASE 2: HTML drag source from browser
    const htmlData = e.dataTransfer.getData("text/html");
    if (htmlData) {
      const doc = new DOMParser().parseFromString(htmlData, "text/html");
      const img = doc.querySelector("img");
      const src = img?.getAttribute("src");

      if (src) {
        try {
          if (src.startsWith("data:image")) {
            const byteString = atob(src.split(",")[1]);
            const mimeType = src.split(",")[0].split(":")[1].split(";")[0];
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            for (let i = 0; i < byteString.length; i++) {
              ia[i] = byteString.charCodeAt(i);
            }
            const blob = new Blob([ab], { type: mimeType });
            const file = new File([blob], "dragged-image.png", { type: mimeType });
            handleFile(file);
            return;
          }

          const response = await fetch(src);
          if (!response.ok) throw new Error("Fetch failed");

          const blob = await response.blob();
          const fetchedFile = new File([blob], "dragged-image.jpg", { type: blob.type });
          handleFile(fetchedFile);
          return;
        } catch (err) {
          console.error("Failed to load dropped image:", err);
          Swal.fire({
            title: "Blocked Image Source",
            text: "This website does not allow dragging images directly. Please save the image first, paste it from clipboard, or drag a local file.",
            icon: "warning",
            showConfirmButton: false,
            timer: 2200,
          });
          return;
        }
      }
    }

    // CASE 3: Plain URL dropped
    const imageUrl =
      e.dataTransfer.getData("text/uri-list") ||
      e.dataTransfer.getData("text/plain");

    if (imageUrl && imageUrl.startsWith("http")) {
      try {
        const response = await fetch(imageUrl);
        if (!response.ok) throw new Error("Fetch failed");

        const blob = await response.blob();
        const fetchedFile = new File([blob], "dragged-image.jpg", { type: blob.type });
        handleFile(fetchedFile);
        return;
      } catch (err) {
        console.error("Failed to load dropped image:", err);
        Swal.fire({
          title: "Blocked Image Source",
          text: "That site blocks direct image dragging. Please save the image first, paste it, or drag a local file instead.",
          icon: "warning",
          showConfirmButton: false,
          timer: 2200,
        });
        return;
      }
    }

    Swal.fire({
      title: "Invalid File",
      text: "Please drag an image file or paste an image.",
      icon: "warning",
      showConfirmButton: false,
      timer: 1500,
    });
  };

  // Handle paste from clipboard (Ctrl+V / ⌘+V)
	const handlePaste = async (e) => {
		e.preventDefault();

		const items = [...e.clipboardData.items];

		// CASE 1: Direct image blob (e.g. “Copy image”)
		const imgItem = items.find((i) => i.type.indexOf("image") === 0);
		if (imgItem) {
			const file = imgItem.getAsFile();
			if (file) {
				handleFile(file);
				return;
			}
		}

		// CASE 2: HTML content with an <img> tag (e.g. LinkedIn or other sites)
		const htmlData = e.clipboardData.getData("text/html");
		if (htmlData) {
			const doc = new DOMParser().parseFromString(htmlData, "text/html");
			const img = doc.querySelector("img");
			if (img) {
				const src = img.getAttribute("src");

				try {
					if (src.startsWith("data:image")) {
						// Convert base64 data directly into a Blob
						const byteString = atob(src.split(",")[1]);
						const mimeType = src.split(",")[0].split(":")[1].split(";")[0];
						const ab = new ArrayBuffer(byteString.length);
						const ia = new Uint8Array(ab);
						for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
						const blob = new Blob([ab], { type: mimeType });
						const file = new File([blob], "pasted-image.png", { type: mimeType });
						handleFile(file);
					} else {
						// External image URL — fetch and convert to File
						const res = await fetch(src);
						if (!res.ok) throw new Error("Fetch failed");
						const blob = await res.blob();
						const file = new File([blob], "pasted-image.png", { type: blob.type });
						handleFile(file);
					}
					return;
				} catch (err) {
					console.error("Error processing pasted HTML image:", err);
					Swal.fire({ title: "Error", text: "Could not load pasted image.", icon: "error", showConfirmButton: false, timer: 1500, });
					return;
				}
			}
		}

		// CASE 3: Plain text with an image URL
		const text = e.clipboardData.getData("text/plain");
		if (text && /^https?:\/\//i.test(text)) {
			try {
				const res = await fetch(text);
				if (!res.ok) throw new Error("Fetch failed");
				const blob = await res.blob();
				const file = new File([blob], "pasted-image.png", { type: blob.type });
				handleFile(file);
				return;
			} catch {
				Swal.fire({ title: "Error", text: "Could not fetch image from pasted link.", icon: "error", showConfirmButton: false, timer: 1500, });
				return;
			}
		}

		// Otherwise — no valid image
		Swal.fire({ title: "No Image Found", text: "Copy an image first, then press Ctrl+V / ⌘+V.", icon: "info", showConfirmButton: false, timer: 1500, });
	};

  return (
    <>
      {/* Main Profile Picture */}
      <div className={`relative group mx-auto ${callingFrom==='Profile' ? 'w-32 h-32' : 'w-24 h-24'} sm:w-36 sm:h-36 mb-4 bg-gray-100 rounded-full border-4 border-white text-gray-400 cursor-pointer`}>
        <img
          src={profilePicturePreview || expertDefaultImg}
          alt="Preview"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = expertDefaultImg;
          }}
          className="w-full h-full rounded-full border-5 border-white object-cover shadow-md"
          onClick={() => setIsModalOpen(true)}
        />
        <div
          className="absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-20 text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          onClick={() => setIsModalOpen(true)}
        >
          Click to edit
        </div>
      </div>

      {/* Popup Modal */}
      <AnimatePresence>
        {isModalOpen && (
          // <motion.div
          //   className="fixed inset-0 bg-black/60 flex justify-center items-center z-50"
          //   initial={{ opacity: 0 }}
          //   animate={{ opacity: 1 }}
          //   exit={{ opacity: 0 }}
          //   onClick={() => setIsModalOpen(false)}
          // >
          //   <motion.div
          //     className="bg-white rounded-3xl shadow-2xl w-[95%] max-w-lg overflow-hidden"
          //     initial={{ scale: 0.96, opacity: 0 }}
          //     animate={{ scale: 1, opacity: 1 }}
          //     exit={{ scale: 0.96, opacity: 0 }}
          //     onClick={(e) => e.stopPropagation()}
          //   >
          //     <div className="relative px-6 py-5 border-b border-gray-100">
          //       <h2 className="text-xl font-semibold text-gray-900">Profile photo</h2>
          //       <p className="text-sm text-gray-500 mt-1">
          //         Crop and adjust your image before saving.
          //       </p>

          //       <button
          //         onClick={() => setIsModalOpen(false)}
          //         className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition"
          //       >
          //         <i className="fa-solid fa-xmark text-base"></i>
          //       </button>
          //     </div>

          //     <div className="p-6">
          //       <div
          //         className="relative w-full h-[320px] rounded-2xl overflow-hidden bg-gray-50 border border-gray-200"
          //         ref={dropArea}
          //         tabIndex={0}
          //         onDragOver={(e) => e.preventDefault()}
          //         onDrop={handleDrop}
          //         onPaste={handlePaste}
          //         onMouseEnter={() => dropArea.current?.focus()}
          //       >
          //         <Cropper
          //           image={selectedImage || expertDefaultImg}
          //           crop={crop}
          //           zoom={zoom}
          //           aspect={1}
          //           cropShape="round"
          //           showGrid={false}
          //           onCropChange={setCrop}
          //           onZoomChange={setZoom}
          //           onCropComplete={onCropComplete}
          //         />
          //       </div>

          //       <div className="mt-5">
          //         <div className="flex items-center justify-between mb-2">
          //           <label className="text-sm font-medium text-gray-700">Zoom</label>
          //           <span className="text-xs text-gray-400">{zoom.toFixed(1)}x</span>
          //         </div>
          //         <input
          //           type="range"
          //           min={1}
          //           max={3}
          //           step={0.1}
          //           value={zoom}
          //           onChange={(e) => setZoom(Number(e.target.value))}
          //           className="w-full accent-blue-600"
          //         />
          //       </div>

          //       <div className="mt-6 flex items-center justify-between gap-3 rounded-2xl bg-gray-50 border border-gray-200 px-4 py-3">
          //         <div>
          //           <p className="text-sm font-medium text-gray-700">Replace photo</p>
          //           <p className="text-xs text-gray-500">Drag, drop, or paste an image</p>
          //         </div>

          //         <button
          //           type="button"
          //           onClick={() => document.getElementById("profileFile").click()}
          //           className="px-4 py-2 rounded-lg bg-white border border-gray-300 hover:bg-gray-100 text-sm font-medium text-gray-700"
          //         >
          //           Upload
          //         </button>

          //         <input
          //           id="profileFile"
          //           type="file"
          //           accept="image/*"
          //           className="hidden"
          //           onChange={(e) => handleFile(e.target.files[0])}
          //         />
          //       </div>
          //     </div>

          //     <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50">
          //       <button
          //         onClick={handleRemovePicture}
          //         className="px-4 py-2 rounded-lg text-red-600 hover:bg-red-50"
          //       >
          //         Remove
          //       </button>

          //       <div className="flex gap-3">
          //         <button
          //           onClick={() => setIsModalOpen(false)}
          //           className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"
          //         >
          //           Cancel
          //         </button>
          //         <button
          //           onClick={handleCropSave}
          //           className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
          //         >
          //           Save
          //         </button>
          //       </div>
          //     </div>
          //   </motion.div>
          // </motion.div>

          <motion.div
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-3 sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div
              className="
                w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl
                max-h-[95vh] overflow-hidden
                rounded-2xl sm:rounded-3xl bg-white shadow-2xl
                flex flex-col
              "
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="relative px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                  Profile photo
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 mt-1 pr-10">
                  Crop and adjust your image before saving.
                </p>

                <button
                  onClick={() => setIsModalOpen(false)}
                  className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 transition"
                >
                  <i className="fa-solid fa-xmark text-sm sm:text-base"></i>
                </button>
              </div>

              <div className="p-4 sm:p-6 overflow-y-auto">
                <div
                  className="
                    relative w-full
                    h-[240px] sm:h-[280px] md:h-[320px]
                    rounded-2xl overflow-hidden
                    bg-gray-50 border border-gray-200
                  "
                  ref={dropArea}
                  tabIndex={0}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                  onPaste={handlePaste}
                  onMouseEnter={() => dropArea.current?.focus()}
                >
                  <Cropper
                    image={selectedImage || expertDefaultImg}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="round"
                    showGrid={false}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                </div>

                <div className="mt-4 sm:mt-5">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Zoom</label>
                    <span className="text-xs text-gray-400">{zoom.toFixed(1)}x</span>
                  </div>
                  <input
                    type="range"
                    min={1}
                    max={3}
                    step={0.1}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="w-full accent-blue-600"
                  />
                </div>

                <div
                  className="
                    mt-5 sm:mt-6
                    flex flex-col sm:flex-row
                    items-start sm:items-center
                    justify-between
                    gap-3
                    rounded-2xl bg-gray-50 border border-gray-200
                    px-4 py-3
                  "
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-700">Replace photo</p>
                    <p className="text-xs text-gray-500">
                      Drag, drop, or paste an image
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => document.getElementById("profileFile").click()}
                    className="
                      w-full sm:w-auto
                      px-4 py-2 rounded-lg
                      bg-white border border-gray-300
                      hover:bg-gray-100
                      text-sm font-medium text-gray-700
                    "
                  >
                    Upload
                  </button>

                  <input
                    id="profileFile"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFile(e.target.files[0])}
                  />
                </div>
              </div>

              <div
                className="
                  px-4 sm:px-6 py-2 border-t border-gray-100 bg-gray-50
                  flex flex-row
                  items-stretch sm:items-center
                  justify-between
                  gap-3
                "
              >
                <button
                  onClick={handleRemovePicture}
                  className="px-4 py-2 rounded-lg text-red-600 hover:bg-red-50 text-left sm:text-center"
                >
                  Remove
                </button>

                <div className="flex flex-row gap-3 w-full sm:w-auto">
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="
                      w-full sm:w-auto
                      px-4 py-2 rounded-lg
                      bg-white border border-gray-300
                      text-gray-700 hover:bg-gray-100
                    "
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCropSave}
                    className="
                      w-full sm:w-auto
                      px-4 py-2 rounded-lg
                      bg-blue-600 hover:bg-blue-700 text-white
                    "
                  >
                    Save
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ProfilePicture;